// 客户端 DevTools 内存分析工具
export default defineNuxtPlugin(() => {
  // 只在开发环境启用
  if (process.env.NODE_ENV !== 'development') {
    return
  }

  // 检查是否启用调试模式 
  const isDebugMode = process.env.DEBUG_MEMORY === 'true' || 
                     process.env.DEV_QUIET === 'true' || 
                     window.location.search.includes('debug=true')

  if (!isDebugMode) {
    return
  }

  // 格式化字节数
  const formatBytes = (bytes: number) => {
    return (bytes / 1024 / 1024).toFixed(2) + ' MB'
  }

  // 获取页面内存使用（近似值）
  const getPageMemoryUsage = () => {
    if ('memory' in performance) {
      const memInfo = (performance as any).memory
      return {
        usedJSHeapSize: memInfo.usedJSHeapSize,
        totalJSHeapSize: memInfo.totalJSHeapSize,
        jsHeapSizeLimit: memInfo.jsHeapSizeLimit,
        formatted: {
          used: formatBytes(memInfo.usedJSHeapSize),
          total: formatBytes(memInfo.totalJSHeapSize),
          limit: formatBytes(memInfo.jsHeapSizeLimit)
        }
      }
    }
    return null
  }

  // DOM节点计数
  const getDOMStats = () => {
    return {
      totalNodes: document.querySelectorAll('*').length,
      images: document.querySelectorAll('img').length,
      scripts: document.querySelectorAll('script').length,
      stylesheets: document.querySelectorAll('link[rel="stylesheet"]').length
    }
  }

  // 创建客户端 devtools 对象
  const clientDevtools = {
    // 获取内存使用情况
    getMemoryUsage: () => {
      const memoryInfo = getPageMemoryUsage()
      const domStats = getDOMStats()
      
      console.group('📊 客户端内存使用情况')
      
      if (memoryInfo) {
        console.log('🔍 JS堆内存:')
        console.log(`  已使用: ${memoryInfo.formatted.used}`)
        console.log(`  总计: ${memoryInfo.formatted.total}`)
        console.log(`  限制: ${memoryInfo.formatted.limit}`)
        console.log(`  使用率: ${((memoryInfo.usedJSHeapSize / memoryInfo.totalJSHeapSize) * 100).toFixed(2)}%`)
      } else {
        console.log('⚠️  浏览器不支持 performance.memory API')
      }
      
      console.log('🏗️  DOM统计:')
      console.log(`  总节点数: ${domStats.totalNodes}`)
      console.log(`  图片数: ${domStats.images}`)
      console.log(`  脚本数: ${domStats.scripts}`)
      console.log(`  样式表数: ${domStats.stylesheets}`)
      
      console.groupEnd()
      
      return { memory: memoryInfo, dom: domStats }
    },

    // 生成堆快照（客户端版本）
    takeHeapSnapshot: () => {
      console.log('📸 客户端堆快照生成指南:')
      console.log('1. 打开 Chrome DevTools (F12)')
      console.log('2. 切换到 "Memory" 标签页')
      console.log('3. 选择 "Heap snapshot"')
      console.log('4. 点击 "Take snapshot" 按钮')
      console.log('5. 等待快照生成完成')
      console.log('')
      console.log('💡 如需服务端堆快照，请在服务端控制台使用相同命令')
      
      // 尝试触发垃圾回收（如果可用）
      if ('gc' in window) {
        (window as any).gc()
        console.log('✅ 已执行客户端垃圾回收')
      }
    },

    // 获取性能条目
    getPerformanceEntries: () => {
      const entries = performance.getEntriesByType('navigation')
      const resources = performance.getEntriesByType('resource')
      
      console.group('⚡ 性能统计')
      console.log(`导航条目: ${entries.length}`)
      console.log(`资源条目: ${resources.length}`)
      
      if (entries.length > 0) {
        const nav = entries[0] as PerformanceNavigationTiming
        console.log('页面加载时间:')
        console.log(`  DOM Content Loaded: ${nav.domContentLoadedEventEnd - nav.domContentLoadedEventStart}ms`)
        console.log(`  Load Event: ${nav.loadEventEnd - nav.loadEventStart}ms`)
      }
      
      console.groupEnd()
      return { navigation: entries, resources }
    },

    // 强制垃圾回收
    forceGC: () => {
      if ('gc' in window) {
        const before = getPageMemoryUsage()
        ;(window as any).gc()
        
        setTimeout(() => {
          const after = getPageMemoryUsage()
          if (before && after) {
            const freed = before.usedJSHeapSize - after.usedJSHeapSize
            console.log(`🗑️  垃圾回收完成`)
            console.log(`释放内存: ${formatBytes(freed)}`)
          }
        }, 100)
      } else {
        console.warn('⚠️  浏览器未暴露gc()接口')
        console.log('💡 要启用gc()，请使用 --js-flags="--expose-gc" 启动Chrome')
      }
    },

    // 监控内存使用
    startMemoryMonitoring: (interval = 5000) => {
      console.log(`🔄 开始内存监控 (间隔: ${interval}ms)`)
      
      const monitor = setInterval(() => {
        const memInfo = getPageMemoryUsage()
        if (memInfo) {
          console.log(`💾 [${new Date().toLocaleTimeString()}] JS堆内存: ${memInfo.formatted.used} / ${memInfo.formatted.total}`)
        }
      }, interval)
      
      // 返回停止函数
      return () => {
        clearInterval(monitor)
        console.log('⏹️  内存监控已停止')
      }
    },

    // 分析页面大对象
    analyzePageObjects: () => {
      console.group('🔍 页面对象分析')
      
      // 分析图片
      const images = Array.from(document.querySelectorAll('img'))
      const largeImages = images.filter(img => {
        return img.naturalWidth > 1920 || img.naturalHeight > 1080
      })
      
      console.log(`📷 图片统计:`)
      console.log(`  总数: ${images.length}`)
      console.log(`  大图片 (>1920x1080): ${largeImages.length}`)
      
      if (largeImages.length > 0) {
        console.log('  大图片列表:')
        largeImages.forEach((img, index) => {
          console.log(`    ${index + 1}. ${img.src} (${img.naturalWidth}x${img.naturalHeight})`)
        })
      }
      
      // 分析缓存
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          console.log(`💾 缓存存储:`)
          console.log(`  缓存数量: ${cacheNames.length}`)
          cacheNames.forEach((name, index) => {
            console.log(`    ${index + 1}. ${name}`)
          })
        })
      }
      
      console.groupEnd()
    },

    // 服务端工具提示
    serverTools: () => {
      console.group('🖥️  服务端内存分析工具')
      console.log('以下命令需要在服务端控制台(Terminal)中使用:')
      console.log('')
      console.log('📊 查看服务端内存统计:')
      console.log('  - 连接到服务端调试: chrome://inspect')
      console.log('  - 在服务端DevTools控制台使用: devtools.getMemoryUsage()')
      console.log('')
      console.log('🔧 服务端专用命令:')
      console.log('  - devtools.takeHeapSnapshot() // 生成.heapsnapshot文件')
      console.log('  - devtools.analyzeMemoryLeak() // 分析内存泄漏')
      console.log('  - devtools.getMemoryHistory() // 查看历史记录')
      console.log('')
      console.log('💡 启动服务端调试模式:')
      console.log('  npm run dev:devtools')
      console.groupEnd()
    },

    // 帮助信息
    help: () => {
      console.group('🔧 DevTools 客户端内存分析工具')
      console.log('可用命令:')
      console.log('  devtools.getMemoryUsage() - 获取内存使用情况')
      console.log('  devtools.takeHeapSnapshot() - 堆快照生成指南') 
      console.log('  devtools.getPerformanceEntries() - 获取性能数据')
      console.log('  devtools.forceGC() - 强制垃圾回收')
      console.log('  devtools.startMemoryMonitoring(5000) - 开始内存监控')
      console.log('  devtools.analyzePageObjects() - 分析页面对象')
      console.log('  devtools.serverTools() - 查看服务端工具说明')
      console.log('  devtools.help() - 显示此帮助')
      console.groupEnd()
    }
  }

  // 将 devtools 挂载到 window 对象
  ;(window as any).devtools = clientDevtools

  // 显示启动信息
  setTimeout(() => {
    console.log('🔧 客户端 DevTools 内存分析工具已就绪')
    console.log('💡 使用 devtools.help() 查看可用命令')
    
    // 如果是静音模式，额外提示
    if (process.env.DEV_QUIET === 'true') {
      console.log('🔇 当前为静音模式，DevTools功能正常可用')
    }
  }, 1000)
})